import { Component, Input } from '@angular/core';
import { CompanyResponse, MembershipPlanResponse, MembershipResponse } from '../../../../@shared/models/response.module';
import { MembershipService } from '../../../../@shared/services/membership-service';
import { AQH_MEM_4, _AQH_MEM_7 } from '../../../../@shared/models/input.module';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AddMembershipPlan } from './components/add-membership-plan/add-membership-plan';
import { EditMembershipPlan } from './components/edit-membership-plan/edit-membership-plan';
import { ViewPlanMembers } from './components/view-plan-members/view-plan-members';
import { ExportMembersModal } from './components/export-members-modal/export-members-modal';
import { forkJoin } from 'rxjs';

interface PlanWithStats extends MembershipPlanResponse {
  memberCount: number;
  activeMemberCount: number;
}

@Component({
  selector: 'app-company-profile-membership-plans',
  standalone: false,
  templateUrl: './company-profile-membership-plans.html',
  styleUrl: './company-profile-membership-plans.css'
})
export class CompanyProfileMembershipPlans {
  @Input() company: CompanyResponse;
  plans: PlanWithStats[] = [];
  memberships: MembershipResponse[] = [];
  loading: boolean = false;

  // Dashboard stats
  totalMembers: number = 0;
  activeMembers: number = 0;
  pendingMembers: number = 0;
  newMembersThisMonth: number = 0;
  growthPercentage: number = 0;

  constructor(
    private membershipService: MembershipService,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    this.loadData();
  }

  loadData() {
    this.loading = true;

    const plansParams: AQH_MEM_4 = {
      company_id: this.company.company_id
    };

    forkJoin({
      plans: this.membershipService.getMembershipPlans(plansParams),
      memberships: this.membershipService.getCompanyMemberships(this.company.company_id)
    }).subscribe({
      next: (result) => {
        this.memberships = result.memberships || [];
        this.plans = (result.plans || []).map(plan => ({
          ...plan,
          memberCount: this.getMemberCountForPlan(plan.plan_id),
          activeMemberCount: this.getActiveMemberCountForPlan(plan.plan_id)
        }));
        this.calculateStats();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  loadPlans() {
    this.loadData();
  }

  getMemberCountForPlan(planId: number): number {
    return this.memberships
      .filter(m => m.plan?.plan_id === planId)
      .reduce((count, m) => count + this.getMembershipMemberCount(m), 0);
  }

  getActiveMemberCountForPlan(planId: number): number {
    return this.memberships
      .filter(m => m.plan?.plan_id === planId && m.status === 'active')
      .reduce((count, m) => count + this.getMembershipMemberCount(m), 0);
  }

  /**
   * Get the actual number of members for a membership.
   * For family memberships, counts all family members.
   * For individual memberships, returns 1.
   */
  getMembershipMemberCount(membership: MembershipResponse): number {
    if (membership.family && membership.family.members && membership.family.members.length > 0) {
      return membership.family.members.length;
    }
    return 1;
  }

  calculateStats() {
    this.totalMembers = this.memberships.reduce((count, m) => count + this.getMembershipMemberCount(m), 0);
    this.activeMembers = this.memberships
      .filter(m => m.status === 'active')
      .reduce((count, m) => count + this.getMembershipMemberCount(m), 0);
    this.pendingMembers = this.memberships
      .filter(m => m.status === 'pending')
      .reduce((count, m) => count + this.getMembershipMemberCount(m), 0);

    // Calculate new members this month
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);

    this.newMembersThisMonth = this.memberships.filter(m => {
      if (!m.created_at) return false;
      const createdAt = new Date(m.created_at);
      return createdAt >= startOfMonth;
    }).length;

    // Calculate growth percentage (comparing this month to last month)
    const lastMonthNew = this.memberships.filter(m => {
      if (!m.created_at) return false;
      const createdAt = new Date(m.created_at);
      return createdAt >= startOfLastMonth && createdAt <= endOfLastMonth;
    }).length;

    if (lastMonthNew > 0) {
      this.growthPercentage = Math.round(((this.newMembersThisMonth - lastMonthNew) / lastMonthNew) * 100);
    } else if (this.newMembersThisMonth > 0) {
      this.growthPercentage = 100;
    } else {
      this.growthPercentage = 0;
    }
  }

  getMembershipsForPlan(planId: number): MembershipResponse[] {
    return this.memberships.filter(m => m.plan?.plan_id === planId);
  }

  openAddPlanModal() {
    let modalRef = this.modalService.open(AddMembershipPlan, { centered: true, size: 'lg' });
    modalRef.componentInstance.company = this.company;
    modalRef.result.then((result) => {
      if (result) {
        this.loadData();
      }
    }).catch(() => {});
  }

  openEditPlanModal(plan: MembershipPlanResponse) {
    let modalRef = this.modalService.open(EditMembershipPlan, { centered: true, size: 'lg' });
    modalRef.componentInstance.plan = plan;
    modalRef.componentInstance.company = this.company;
    modalRef.result.then((result) => {
      if (result) {
        this.loadData();
      }
    }).catch(() => {});
  }

  openViewMembersModal(plan: PlanWithStats) {
    const modalRef = this.modalService.open(ViewPlanMembers, {
      centered: true,
      size: 'xl'
    });
    modalRef.componentInstance.plan = plan;
    modalRef.componentInstance.memberships = this.getMembershipsForPlan(plan.plan_id);
    modalRef.componentInstance.companyId = this.company.company_id;

    // Reload data when modal closes (in case a new member was added)
    modalRef.result.then(() => {
      this.loadData();
    }).catch(() => {
      // Still reload in case a member was added before dismissing
      this.loadData();
    });
  }

  openExportModal() {
    const modalRef = this.modalService.open(ExportMembersModal, {
      centered: true,
      size: 'lg'
    });
    modalRef.componentInstance.plans = this.plans;
    modalRef.componentInstance.memberships = this.memberships;
    modalRef.componentInstance.company = this.company;
  }

  deletePlan(plan: MembershipPlanResponse) {
    if (!confirm(`Are you sure you want to delete the plan "${plan.name}"?`)) {
      return;
    }

    const params: _AQH_MEM_7 = {
      plan_id: plan.plan_id
    };

    this.membershipService.deleteMembershipPlan(params).subscribe({
      next: () => {
        this.loadData();
      }
    });
  }

  formatPrice(cents: number): string {
    return (cents / 100).toFixed(2);
  }

  /**
   * Check if subscription is required but not active
   * Only riding clubs require subscription for memberships
   */
  needsSubscription(): boolean {
    return this.company?.company_type === 'riding_club' && !this.company?.subscription_active;
  }
}
